/**
 * Admin web terminal — the browser side.
 *
 * A full-screen, admin-only panel that walks the multi-root open gauntlet,
 * then mounts up to `max_tabs` xterm.js terminal instances wired to server
 * PTYs over WebSockets. Each session is authorized by a single-use token from
 * the open flow — never by the cookie. See features/admin_terminal.md.
 *
 * Tab lifecycle:
 *   - Fresh open: start with 1 tab (session "1") only — prevents simultaneous
 *     WS racing on MacOS / high-latency connections.
 *   - "+" button: opens the next available session (up to max_tabs from server).
 *   - "×" on tab: closes the WS, kills the PTY shell on the host.
 *   - WS close (idle timeout / shell exit): tab turns dead (xterm gone), stays
 *     visible so the user can read the error, then close via "×".
 *   - Resume: opens WS only for sessions already running on the host.
 *
 * Phases:
 *   loading      — checking enrollment
 *   disabled     — terminal not enabled on the server (404)
 *   enroll       — register a passkey (step-up: existing password + TOTP)
 *   intro        — armed? then "Open terminal"
 *   credentials  — passkey (WebAuthn) AND password → server sends Telegram code
 *   codes        — Google TOTP AND Telegram code → ws_tokens (Phase 8: 3 tokens)
 *   connected    — live xterm(s) over PTY socket(s)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { api, ApiError, wsUrl } from "@/api/client";
import { useAppSelector } from "@/state/hooks";
import styles from "./TerminalPanel.module.css";

type Phase =
  | "loading"
  | "disabled"
  | "enroll"
  | "intro"
  | "credentials"
  | "codes"
  | "connected";

type SessionId = "1" | "2" | "3";
const ALL_SESSION_IDS: SessionId[] = ["1", "2", "3"];

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

// --- the live terminal (xterm + PTY WebSocket) -------------------------

const DARK_XTERM_THEME = {
  background: "#0a0e16",
  foreground: "#d6deeb",
  cursor: "#d6deeb",
  // Without these xterm draws no visible highlight on a drag-select — the
  // selection model still captures the text (Copy works), but you can't see
  // what you're selecting. A bright translucent blue reads on the near-black bg.
  selectionBackground: "#2d4f7caa",
  selectionInactiveBackground: "#2a3a55aa",
  black: "#1b2230",
  red: "#f7768e",
  green: "#9ece6a",
  yellow: "#e0af68",
  blue: "#7aa2f7",
  magenta: "#bb9af7",
  cyan: "#7dcfff",
  white: "#c0caf5",
  brightBlack: "#414868",
  brightRed: "#f7768e",
  brightGreen: "#9ece6a",
  brightYellow: "#e0af68",
  brightBlue: "#7aa2f7",
  brightMagenta: "#bb9af7",
  brightCyan: "#7dcfff",
  brightWhite: "#ffffff",
};

const LIGHT_XTERM_THEME = {
  background: "#ffffff",
  foreground: "#1a1a1a",
  cursor: "#1a1a1a",
  // See the dark theme note — a soft blue highlight that reads on white.
  selectionBackground: "#add6ff",
  selectionInactiveBackground: "#dbeafe",
  black: "#24292e",
  red: "#cb2431",
  green: "#22863a",
  yellow: "#b08800",
  blue: "#0366d6",
  magenta: "#6f42c1",
  cyan: "#1b7c83",
  white: "#6a737d",
  brightBlack: "#959da5",
  brightRed: "#cb2431",
  brightGreen: "#22863a",
  brightYellow: "#b08800",
  brightBlue: "#0366d6",
  brightMagenta: "#6f42c1",
  brightCyan: "#1b7c83",
  brightWhite: "#24292e",
};

function TerminalView({
  token,
  mode,
  session,
  dark,
  onClosed,
  onConnected,
  onActivity,
}: {
  token: string;
  mode: "new" | "resume";
  session: SessionId;
  dark: boolean;
  onClosed: (msg: string) => void;
  onConnected?: () => void;
  onActivity?: () => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: 'Menlo, Consolas, "DejaVu Sans Mono", monospace',
      theme: dark ? DARK_XTERM_THEME : LIGHT_XTERM_THEME,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    fit.fit();

    const ws = new WebSocket(
      wsUrl(
        `/api/admin/terminal/ws?token=${encodeURIComponent(token)}&mode=${mode}&session=${session}`,
      ),
    );
    let closedMsg = "Session ended.";
    let pingTimer: number | undefined;

    // --- copy / paste -----------------------------------------------------
    // xterm has no built-in clipboard, so wire the two standard terminal
    // shortcuts. Ctrl+Shift+C copies the highlighted selection; Ctrl+Shift+V
    // pastes into the PTY. Plain Ctrl+C / Ctrl+V are left untouched (SIGINT /
    // xterm's native paste). The handler early-returns on anything that isn't
    // Ctrl+Shift, so it adds no typing lag.
    //
    // Paste MUST go through `term.paste()`, never straight to the socket:
    // paste() converts newlines to CR and, when the app has enabled bracketed
    // paste, wraps the text in ESC[200~ / ESC[201~ so the app treats it as ONE
    // paste. Writing the raw clipboard to the PTY makes a multi-line paste
    // arrive as N separate line entries — a TUI (claude) then keeps only the
    // last few lines and silently drops the head.
    term.attachCustomKeyEventHandler((e) => {
      if (e.type !== "keydown" || !e.ctrlKey || !e.shiftKey) return true;
      const k = e.key.toLowerCase();
      if (k === "c") {
        const text = term.getSelection();
        if (text) navigator.clipboard?.writeText(text).catch(() => {});
        return false;
      }
      if (k === "v") {
        navigator.clipboard
          ?.readText()
          .then((t) => {
            if (t) term.paste(t);
          })
          .catch(() => {});
        return false;
      }
      return true;
    });

    const sendResize = () => {
      try {
        fit.fit();
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
        }
      } catch {
        /* ignore */
      }
    };

    ws.onopen = () => {
      onConnectedRef.current?.();
      sendResize();
      term.focus();
      pingTimer = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 60_000);
    };
    ws.onmessage = (e) => {
      if (typeof e.data === "string") {
        term.write(e.data);
        onActivityRef.current?.();
      }
    };
    ws.onerror = () => {
      closedMsg = "Connection error.";
    };
    ws.onclose = () => {
      clearInterval(pingTimer);
      onClosedRef.current(closedMsg);
    };

    const dataSub = term.onData((d) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "input", data: d }));
      }
    });

    const ro = new ResizeObserver(() => sendResize());
    ro.observe(host);

    return () => {
      clearInterval(pingTimer);
      ro.disconnect();
      dataSub.dispose();
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      term.dispose();
    };
    // token only — re-runs only when a new token is issued (new WS needed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return <div className={styles.xterm} ref={hostRef} />;
}

// --- the modal / auth flow ---------------------------------------------

export function TerminalPanel({
  active,
  refreshKey = 0,
  onConnectedChange,
}: {
  active: boolean;
  refreshKey?: number;
  onConnectedChange?: (connected: boolean) => void;
}) {
  const dark = useAppSelector((s) => s.appearance.theme) !== "light";

  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flowId, setFlowId] = useState("");
  const passkeyOptsRef = useRef<unknown>(null);

  // ws tokens per session; empty string = no token (not yet opened or dead).
  const [wsTokens, setWsTokens] = useState<Record<SessionId, string>>({
    "1": "", "2": "", "3": "",
  });
  // Which session tabs are visible (alive or dead). Starts empty; populated at connect.
  const [activeSessions, setActiveSessions] = useState<SessionId[]>([]);
  // Per-tab mode: "new" spawns a fresh shell, "resume" attaches to existing or spawns.
  const [sessionModes, setSessionModes] = useState<Record<SessionId, "new" | "resume">>({
    "1": "new", "2": "new", "3": "new",
  });
  // Which tab is currently visible.
  const [activeTab, setActiveTab] = useState<SessionId>("1");
  // Unread activity dots per tab.
  const [unread, setUnread] = useState<Record<SessionId, boolean>>({
    "1": false, "2": false, "3": false,
  });
  // Max tabs from server config (TICKVIEW_TERMINAL_MAX_TABS, default 3).
  const [maxTabs, setMaxTabs] = useState(3);
  // Sessions waiting to be opened after the current one's WS connects.
  // Sequential open prevents simultaneous WS upgrades racing on single-use tokens.
  const pendingSessionsRef = useRef<SessionId[]>([]);
  // Ref-copy of activeTab so handleActivity can read it without being in its dep list.
  const activeTabRef = useRef<SessionId>("1");

  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [code, setCode] = useState("");
  const [enrollPw, setEnrollPw] = useState("");
  const [enrollTotp, setEnrollTotp] = useState("");
  const [mode, setMode] = useState<"new" | "resume">("new");
  const [sessionInfo, setSessionInfo] = useState<
    { host_up: boolean; sessions: Record<string, boolean>; max_tabs?: number } | null
  >(null);

  const disconnect = useCallback(() => {
    // Drop the web session only — PTY shells survive so the admin can resume later.
    // Shells are killed only on explicit tab close or "Start new".
    sessionStorage.removeItem("term_reconnect");
    setWsTokens({ "1": "", "2": "", "3": "" });
    setActiveSessions([]);
    setError(null);
    setPhase("intro");
  }, []);

  useEffect(() => {
    setError(null);
    setPassword("");
    setTotp("");
    setCode("");
    setWsTokens({ "1": "", "2": "", "3": "" });
    setActiveSessions([]);
    setPhase("loading");
    api
      .terminalEnrollStatus()
      .then((st) => setPhase(st.fully_enrolled ? "intro" : "enroll"))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setPhase("disabled");
        else {
          setError(errMsg(e));
          setPhase("disabled");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!active || phase !== "intro") return;
    setSessionInfo(null);
    api
      .terminalSession()
      .then((info) => {
        setSessionInfo(info);
        if (info.max_tabs) setMaxTabs(Math.min(info.max_tabs, 3));
      })
      .catch(() => setSessionInfo({ host_up: false, sessions: {} }));
  }, [active, phase, refreshKey]);

  useEffect(() => {
    onConnectedChange?.(phase === "connected");
  }, [phase, onConnectedChange]);

  // When a shell tab's WS closes (idle/exit/error): clear its token so the
  // xterm unmounts, but KEEP the tab visible so the user can read the error
  // and close it via "×". Do NOT go back to intro automatically.
  const onTermClosed = useCallback(
    (sid: SessionId) => (msg: string) => {
      setWsTokens((prev) => ({ ...prev, [sid]: "" }));
      setError(`Shell ${sid}: ${msg} — close the tab or add a new one.`);
    },
    [],
  );

  const handleActivity = useCallback((sid: SessionId) => {
    if (activeTabRef.current !== sid) {
      setUnread((prev) => {
        if (prev[sid]) return prev; // already marked — no re-render needed
        return { ...prev, [sid]: true };
      });
    }
  }, []);

  const switchTab = useCallback((sid: SessionId) => {
    setActiveTab(sid);
    setUnread((prev) => ({ ...prev, [sid]: false }));
  }, []);

  // Called by TerminalView when its WS opens. Opens the next pending session so
  // sessions connect one-by-one instead of all at once (prevents token races).
  const onSessionConnected = useCallback(() => {
    const next = pendingSessionsRef.current.shift();
    if (next) {
      setActiveSessions((prev) => [...prev, next]);
    }
  }, []);

  // Close a tab: kill the shell on the host, remove from visible tabs, clear any error.
  function closeTab(sid: SessionId) {
    setActiveSessions((prev) => {
      const next = prev.filter((s) => s !== sid);
      setActiveTab((cur) => (cur === sid && next.length > 0 ? next[next.length - 1] : cur));
      return next;
    });
    setWsTokens((prev) => ({ ...prev, [sid]: "" }));
    setError(null);
    api.terminalKillSession(sid).catch(() => {});
  }

  // Add a new tab: get a fresh token via /reconnect, open the next session.
  async function addTab() {
    const sessionIds = ALL_SESSION_IDS.slice(0, maxTabs);
    const nextSid = sessionIds.find((sid) => !activeSessions.includes(sid));
    if (!nextSid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const reconnectTok = sessionStorage.getItem("term_reconnect") ?? "";
      const r = await api.terminalReconnect(reconnectTok);
      // Only update the new session's token; don't touch already-connected ones.
      setWsTokens((prev) => ({ ...prev, [nextSid]: r.ws_tokens[nextSid] ?? "" }));
      setSessionModes((prev) => ({ ...prev, [nextSid]: "resume" }));
      setActiveSessions((prev) => [...prev, nextSid]);
      setActiveTab(nextSid);
    } catch (e) {
      setError(`Add tab failed: ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  }

  // Re-attach to an existing shell on a dead tab (WS dropped but shell still alive).
  async function reconnectTab(sid: SessionId) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const reconnectTok = sessionStorage.getItem("term_reconnect") ?? "";
      const r = await api.terminalReconnect(reconnectTok);
      setSessionModes((prev) => ({ ...prev, [sid]: "resume" }));
      setWsTokens((prev) => ({ ...prev, [sid]: r.ws_tokens[sid] ?? "" }));
    } catch (e) {
      setError(`Reconnect failed: ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  }

  // Determine the initial sessions to open after auth.
  function initialSessions(forMode: "new" | "resume"): SessionId[] {
    if (forMode === "resume" && sessionInfo?.sessions) {
      const running = ALL_SESSION_IDS.slice(0, maxTabs).filter(
        (sid) => sessionInfo.sessions[sid],
      );
      if (running.length > 0) return running;
    }
    return ["1"];
  }

  async function doEnroll() {
    setBusy(true);
    setError(null);
    try {
      const { options } = await api.terminalEnrollBegin(enrollPw, enrollTotp);
      const reg = await startRegistration({
        optionsJSON: options as PublicKeyCredentialCreationOptionsJSON,
      });
      const res = await api.terminalEnrollFinish(reg);
      setEnrollPw("");
      setEnrollTotp("");
      if (res.fully_enrolled) setPhase("intro");
      else setError("Passkey registered, but password/TOTP are still missing — set them at the box.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function doBegin() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.terminalOpenBegin();
      setFlowId(r.flow_id);
      passkeyOptsRef.current = r.passkey_options;
      setPhase("credentials");
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError("The terminal isn't available right now.");
      } else setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function doCredentials() {
    setBusy(true);
    setError(null);
    try {
      const assertion = await startAuthentication({
        optionsJSON: passkeyOptsRef.current as PublicKeyCredentialRequestOptionsJSON,
      });
      const r = await api.terminalOpenCredentials(flowId, password, assertion);
      setPassword("");
      if (r.code_sent) setPhase("codes");
    } catch (e) {
      setError(`${errMsg(e)} — start again.`);
      setPhase("intro");
    } finally {
      setBusy(false);
    }
  }

  async function doCodes() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.terminalOpenCodes(flowId, totp, code);
      setTotp("");
      setCode("");
      if (r.reconnect_token) {
        sessionStorage.setItem("term_reconnect", r.reconnect_token);
      }
      const tokens = { "1": r.ws_tokens["1"] ?? "", "2": r.ws_tokens["2"] ?? "", "3": r.ws_tokens["3"] ?? "" };
      setWsTokens(tokens);
      const m = mode === "resume" ? "resume" : "new";
      const modes: Record<SessionId, "new" | "resume"> = { "1": m, "2": m, "3": m };
      setSessionModes(modes);
      if (m === "new") {
        // "Start new" kills ALL existing shells before spawning fresh.
        ALL_SESSION_IDS.slice(0, maxTabs).forEach((sid) =>
          api.terminalKillSession(sid).catch(() => {})
        );
      }
      const sessions = initialSessions(m);
      // Open only the first session; the rest are queued and opened one-by-one
      // as each WS connects (onSessionConnected) to avoid simultaneous token races.
      pendingSessionsRef.current = sessions.slice(1);
      setActiveSessions([sessions[0]]);
      setActiveTab(sessions[0]);
      setPhase("connected");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function doReconnect() {
    setBusy(true);
    setError(null);
    const token = sessionStorage.getItem("term_reconnect") ?? "";
    try {
      const r = await api.terminalReconnect(token);
      setMode("resume");
      const tokens = { "1": r.ws_tokens["1"] ?? "", "2": r.ws_tokens["2"] ?? "", "3": r.ws_tokens["3"] ?? "" };
      setWsTokens(tokens);
      setSessionModes({ "1": "resume", "2": "resume", "3": "resume" });
      const sessions = initialSessions("resume");
      pendingSessionsRef.current = sessions.slice(1);
      setActiveSessions([sessions[0]]);
      setActiveTab(sessions[0]);
      setPhase("connected");
    } catch (e) {
      sessionStorage.removeItem("term_reconnect");
      setError(`Reconnect failed (${errMsg(e)}) — use full auth.`);
    } finally {
      setBusy(false);
    }
  }

  activeTabRef.current = activeTab;

  const anySessionRunning = sessionInfo?.sessions
    ? Object.values(sessionInfo.sessions).some(Boolean)
    : false;
  const reconnectToken = sessionStorage.getItem("term_reconnect");
  const sessionIds = ALL_SESSION_IDS.slice(0, maxTabs);
  const canAddTab = activeSessions.length < maxTabs &&
    sessionIds.some((sid) => !activeSessions.includes(sid));

  return (
    <div className={styles.panelInline}>
      <div className={styles.header}>
        <span className={styles.title}>
          <span className={styles.dot} data-live={phase === "connected"} /> Admin terminal
        </span>
        {phase === "connected" ? (
          <button type="button" className={styles.close} onClick={disconnect}>
            Disconnect
          </button>
        ) : null}
      </div>

      {error ? (
        <div className={styles.error}>
          {error}
          <button
            type="button"
            className={styles.errorDismiss}
            onClick={() => setError(null)}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      ) : null}

      {phase === "connected" ? (
        <div className={styles.tabBar}>
          {activeSessions.map((sid) => (
            <button
              key={sid}
              type="button"
              className={`${styles.tab} ${activeTab === sid ? styles.tabActive : ""}`}
              onClick={() => switchTab(sid)}
            >
              {wsTokens[sid] ? <span className={styles.tabLive} /> : null}
              Shell {sid}
              {unread[sid] && activeTab !== sid ? <span className={styles.tabUnread} /> : null}
              {!wsTokens[sid] ? (
                <span
                  className={styles.tabReconnect}
                  title="Reconnect to shell"
                  onClick={(e) => { e.stopPropagation(); void reconnectTab(sid); }}
                >
                  ↺
                </span>
              ) : null}
              <span
                className={styles.tabClose}
                title="Close tab"
                onClick={(e) => { e.stopPropagation(); closeTab(sid); }}
              >
                ×
              </span>
            </button>
          ))}
          {canAddTab ? (
            <button
              type="button"
              className={styles.tabAdd}
              title="Add shell tab"
              onClick={addTab}
              disabled={busy}
            >
              {busy ? "…" : "+"}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={styles.body}>
        {phase === "loading" && <p className={styles.muted}>Checking…</p>}

        {phase === "disabled" && (
          <p className={styles.muted}>
            The terminal isn't available. It's disabled on the server, or your account
            can't use it.
          </p>
        )}

        {phase === "enroll" && (
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy) doEnroll();
            }}
          >
            <p className={styles.muted}>
              Register a passkey for this device. Confirm with your terminal password and
              Google Authenticator code (set at the box).
            </p>
            <input
              className={styles.input}
              type="password"
              placeholder="Terminal password"
              autoComplete="off"
              value={enrollPw}
              onChange={(e) => setEnrollPw(e.target.value)}
            />
            <input
              className={styles.input}
              inputMode="numeric"
              placeholder="Authenticator code"
              autoComplete="off"
              value={enrollTotp}
              onChange={(e) => setEnrollTotp(e.target.value)}
            />
            <button type="submit" className={styles.primary} disabled={busy}>
              {busy ? "…" : "Register passkey"}
            </button>
          </form>
        )}

        {phase === "intro" && (
          <div className={styles.form}>
            {sessionInfo === null ? (
              <p className={styles.muted}>Checking the terminal host…</p>
            ) : !sessionInfo.host_up ? (
              <p className={styles.muted}>
                The terminal host isn't reachable — make sure the{" "}
                <code>tickview-terminal</code> process is running.
              </p>
            ) : anySessionRunning ? (
              <>
                <p className={styles.muted}>
                  A session is still running. <strong>Resume</strong> it (your
                  live shell + scrollback), or start fresh — which{" "}
                  <strong>kills</strong> the running one. Either way you verify
                  all factors first.
                </p>
                {reconnectToken ? (
                  <button
                    className={styles.primary}
                    disabled={busy}
                    onClick={doReconnect}
                  >
                    {busy ? "…" : "Quick reconnect (skip gauntlet)"}
                  </button>
                ) : null}
                <button
                  className={reconnectToken ? styles.secondary : styles.primary}
                  disabled={busy}
                  onClick={() => {
                    setMode("resume");
                    doBegin();
                  }}
                >
                  {busy ? "…" : "Resume previous session"}
                </button>
                <button
                  className={styles.secondary}
                  disabled={busy}
                  onClick={() => {
                    setMode("new");
                    doBegin();
                  }}
                >
                  Start new (discard running)
                </button>
              </>
            ) : (
              <>
                <p className={styles.muted}>
                  No session running. Opening verifies your passkey + password,
                  then an authenticator code and a one-time Telegram code.
                </p>
                <button
                  className={styles.primary}
                  disabled={busy}
                  onClick={() => {
                    setMode("new");
                    doBegin();
                  }}
                >
                  {busy ? "…" : "Open terminal"}
                </button>
              </>
            )}
          </div>
        )}

        {phase === "credentials" && (
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy) doCredentials();
            }}
          >
            <p className={styles.muted}>
              Verify with your passkey and password. You'll be prompted for the passkey on
              Continue.
            </p>
            <input
              className={styles.input}
              type="password"
              placeholder="Terminal password"
              autoComplete="off"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" className={styles.primary} disabled={busy}>
              {busy ? "Verifying…" : "Continue with passkey"}
            </button>
          </form>
        )}

        {phase === "codes" && (
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy) doCodes();
            }}
          >
            <p className={styles.muted}>
              Enter your Google Authenticator code and the one-time code just sent to
              Telegram.
            </p>
            <input
              className={styles.input}
              inputMode="numeric"
              placeholder="Authenticator code"
              autoComplete="off"
              autoFocus
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
            />
            <input
              className={styles.input}
              inputMode="numeric"
              placeholder="Telegram code"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button type="submit" className={styles.primary} disabled={busy}>
              {busy ? "Opening…" : "Open session"}
            </button>
          </form>
        )}

        {phase === "connected" ? (
          <div className={styles.shellViews}>
            {activeSessions.map((sid) => (
              <div
                key={sid}
                className={`${styles.shellView} ${activeTab !== sid ? styles.shellViewHidden : ""}`}
              >
                {wsTokens[sid] ? (
                  <TerminalView
                    token={wsTokens[sid]}
                    mode={sessionModes[sid] ?? "resume"}
                    session={sid}
                    dark={dark}
                    onClosed={onTermClosed(sid)}
                    onConnected={onSessionConnected}
                    onActivity={() => handleActivity(sid)}
                  />
                ) : (
                  <div className={styles.deadSession}>
                    <p className={styles.muted}>Shell {sid} disconnected.</p>
                    <div className={styles.deadActions}>
                      <button
                        type="button"
                        className={styles.secondary}
                        onClick={() => void reconnectTab(sid)}
                        disabled={busy}
                      >
                        {busy ? "…" : "↺ Reconnect to shell"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
